import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { snakeCase } from 'lodash';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import classNames from 'classnames';
import RefinementValues from './RefinementValues';
import createUniqueId from '../../../shared/helpers/createUniqueId';
import isEnterOrSpace from '../../../shared/helpers/isEnterOrSpace';


class Refinements extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isInfoCollapsed: false,
      openRefinements: this.setOpenRefinements(),
    };
    this.toggleFiltersCollapseStatus = this.toggleFiltersCollapseStatus.bind(this);
    this.toggleInfoCollapseStatus = this.toggleInfoCollapseStatus.bind(this);
  }

  static getRefinementId(refinement) {
    return snakeCase(refinement.values[0].id || refinement.displayName);
  }

  setOpenRefinements() {
    const { refinements } = this.props;

    if (!refinements) {
      return {};
    }

    return refinements.reduce((accumulator, refinement) => {
      if (!refinement.values && !refinement.values[0]) {
        return false;
      }

      return {
        ...accumulator,
        [Refinements.getRefinementId(refinement)]: true,
      };
    }, {});
  }

  toggleFiltersCollapseStatus(refinement) {
    const { openRefinements } = this.state;
    const refinementId = Refinements.getRefinementId(refinement);

    this.setState({
      openRefinements: {
        ...openRefinements,
        [refinementId]: !openRefinements[refinementId],
      },
    });
  }

  toggleInfoCollapseStatus() {
    const { isInfoCollapsed } = this.state;

    this.setState({ isInfoCollapsed: !isInfoCollapsed });
  }

  render() {
    const {
      refinements,
      refinementHandler,
      focusedFilterRef,
      focusedFilter,
      setFocusedFilter,
      origin,
      theme,
    } = this.props;
    const { openRefinements } = this.state;
    const refinementValuesDescribedBy = 'results-grid__refinement-label';
    const refinementsAvailable = refinements && !!refinements.length && refinements.length > 0;

    return (
      <>
        {
          refinementsAvailable &&
            (
              <VisuallyHidden>
                <div id={refinementValuesDescribedBy}>
                  Select an option to instantly narrow your results
                </div>
              </VisuallyHidden>
            )
        }
        {
          refinementsAvailable &&
            refinements.map((refinement) => {
              const groupId = createUniqueId(refinement.displayName);
              const labelId = createUniqueId(refinement.displayName, '-label');
              const contentId = createUniqueId(refinement.displayName, '-content');
              const refinementId = Refinements.getRefinementId(refinement);
              const { displayName, values } = refinement;

              const titleClasses = classNames({
                'results-grid__refinement-title': true,
                [`results-grid__refinement-title--${theme}`]: theme,
              });

              return (
                <div
                  key={groupId}
                  className="results-grid__refinement"
                  aria-labelledby={labelId}
                >
                  <h3 className={titleClasses} id={labelId}>
                    {displayName}
                    <span
                      className={`results-grid__sorting-icon-${openRefinements[refinementId] ? 'down' : 'up'}`}
                      onClick={() => { this.toggleFiltersCollapseStatus(refinement); }}
                      onKeyPress={(event) => {
                        if (!isEnterOrSpace(event)) {
                          return;
                        }

                        event.preventDefault();
                        this.toggleFiltersCollapseStatus(refinement);
                      }}
                      role="button"
                      aria-label={openRefinements[refinementId] ? `hide ${displayName} options` : `expand ${displayName} options`}
                      aria-expanded={!!openRefinements[refinementId]}
                      aria-controls={contentId}
                      tabIndex={0}
                    />
                  </h3>
                  {
                    openRefinements[refinementId] && (
                      <div id={contentId} role="group" aria-label={displayName} aria-describedby={refinementValuesDescribedBy}>
                        <RefinementValues
                          group={refinementId}
                          values={values}
                          refinementHandler={refinementHandler}
                          focusedRef={focusedFilterRef}
                          focused={focusedFilter}
                          setFocused={setFocusedFilter}
                          origin={origin}
                          theme={theme}
                        />
                      </div>
                    )
                  }
                </div>
              );
            })
        }
      </>
    );
  }
}


Refinements.propTypes = {
  focusedFilter: PropTypes.shape({}),
  focusedFilterRef: PropTypes.shape({}).isRequired,
  origin: PropTypes.string,
  refinementHandler: PropTypes.func.isRequired,
  refinements: PropTypes.arrayOf(PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  })).isRequired,
  setFocusedFilter: PropTypes.func.isRequired,
  theme: PropTypes.string,
};

Refinements.defaultProps = {
  focusedFilter: null,
  origin: '',
  theme: undefined,
};

export default Refinements;
